import { computed, ref } from 'vue';

import { httpService } from '../services/httpService';
import { config } from '../services/configService';
import { parseSugestaoDeResposta, type AnaliseDeChamado } from './aiAgentContract';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Chamados_ListaResponse';
import '../contracts/Chamados_DetalheResponse';
import '../contracts/Chamados_FranquiasResponse';
import '../contracts/Chamados_AbrirInput';
import '../contracts/Chamados_ResponderInput';
import '../contracts/Chamados_MudarStatusInput';
import '../contracts/Chamados_MarcarAgenteInput';
import '../contracts/Chamados_AnexarInput';

import type { Chamados_ChamadoRow } from '../contracts/Chamados_ChamadoRow';
import type { Chamados_MensagemRow } from '../contracts/Chamados_MensagemRow';
import type { Chamados_FranquiaRow } from '../contracts/Chamados_FranquiaRow';
import type { Chamados_AbrirInput } from '../contracts/Chamados_AbrirInput';
import type { Chamados_ResponderInput } from '../contracts/Chamados_ResponderInput';
import type { Chamados_MudarStatusInput } from '../contracts/Chamados_MudarStatusInput';
import type { Chamados_MarcarAgenteInput } from '../contracts/Chamados_MarcarAgenteInput';

/**
 * useChamados — o canal de suporte franquia → franqueadora.
 * Plano: docs/plans/chamados-plan.md (F1).
 *
 * ---------------------------------------------------------------------------
 * O QUE ESTE COMPOSABLE NÃO FAZ, DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * Ele NÃO decide quem pode o quê. `souSuporte` e `transicoesPossiveis` vêm
 * PRONTOS do backend, e a tela só os consome.
 *
 * A tentação natural seria derivar "sou suporte" dos grupos do JWT aqui no
 * front, e "quais botões de status mostrar" de uma cópia da máquina de estados.
 * As duas seriam uma SEGUNDA fonte da mesma regra — e a segunda fonte diverge
 * no dia em que só uma das duas é atualizada. Pior: divergiria em silêncio, com
 * a tela oferecendo um botão que o backend recusa (irritante) ou escondendo um
 * que ele aceita (invisível, e ninguém reporta o que não vê).
 *
 * O backend já decidiu a cada request. Aqui a gente só desenha.
 *
 * ---------------------------------------------------------------------------
 * AS FRANQUIAS DO SELETOR VÊM DE /chamados/franquias, NÃO DO FILTRO GLOBAL
 * ---------------------------------------------------------------------------
 * O seletor de franquia padrão da plataforma oferece as franquias LIBERADAS
 * POR BILLING. Aqui isso seria um defeito grave: a franquia inadimplente
 * abriria a tela de chamado com o seletor vazio e não conseguiria pedir socorro
 * exatamente no dia em que mais precisa ("meu sistema travou, não consigo
 * faturar"). O endpoint próprio lê da IDENTIDADE do usuário.
 */

const URL_LISTA = '/index.php/chamados/lista';
const URL_DETALHE = '/index.php/chamados/detalhe';
const URL_ABRIR = '/index.php/chamados/abrir';
const URL_RESPONDER = '/index.php/chamados/responder';
const URL_MUDAR_STATUS = '/index.php/chamados/mudar-status';
const URL_MARCAR_AGENTE = '/index.php/chamados/marcar-agente';
const URL_FRANQUIAS = '/index.php/chamados/franquias';
const URL_ANEXAR = '/index.php/chamados/anexar';

/**
 * Os limites do anexo, ESPELHADOS do backend (`Chamados::MIMES_ACEITOS`,
 * `TAMANHO_MAX`, `ANEXOS_MAX_POR_MENSAGEM`).
 *
 * Espelho, não fonte: quem recusa de verdade é o backend, que lê os BYTES do
 * arquivo — aqui só se evita a viagem inútil e se dá um erro imediato. Um
 * `accept` no input não valida nada (o seletor do sistema operacional aceita
 * "todos os arquivos" com um clique), e o `type` do File vem do sistema
 * operacional do usuário, que é justamente o que não se pode acreditar.
 */
export const MIMES_DE_IMAGEM = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const TAMANHO_MAX_ANEXO = 5 * 1024 * 1024;
export const MAX_ANEXOS_POR_MENSAGEM = 5;

/** As abas da tela. Espelham o particionamento do enum no backend. */
export type AbaChamados = 'em_andamento' | 'encerrados' | 'todos';

/**
 * As formas de entrada e de linha vem DOS CONTRATOS GERADOS, nunca redeclaradas
 * aqui.
 *
 * Eu tinha escrito `FranquiaDoSeletor` e `NovoChamado` a mao, com os mesmos
 * campos — e o vue-tsc reclamou na hora em que o gerador passou a produzir o
 * tipo de verdade. Uma interface local espelhando um contrato e uma copia que
 * NAO quebra quando o contrato muda: ela simplesmente passa a mentir, e o
 * compilador nao tem como saber que deveriam ser a mesma coisa.
 */
export type FranquiaDoSeletor = Chamados_FranquiaRow;
export type NovoChamado = Chamados_AbrirInput;
export type MensagemDoFio = Chamados_MensagemRow;

/**
 * Teto do assunto. É o teto REAL da coluna (`varchar(100)`), não um número de
 * gosto — e o mesmo valor que `Chamados_AbrirInput::MAX_ASSUNTO` recusa no
 * backend.
 *
 * Aqui ele serve para o campo AVISAR ENQUANTO SE DIGITA. O backend recusa de
 * qualquer jeito; o que este número evita é o usuário escrever 140 caracteres,
 * mandar, e só então descobrir. Sem os dois lados, ou a tela mente ou o
 * servidor humilha.
 */
export const MAX_ASSUNTO = 100;

export function useChamados() {
  const carregando = ref(false);
  const erro = ref<string | null>(null);

  const chamados = ref<Chamados_ChamadoRow[]>([]);
  const aba = ref<AbaChamados>('em_andamento');

  const chamadoAberto = ref<Chamados_ChamadoRow | null>(null);
  const mensagens = ref<Chamados_MensagemRow[]>([]);
  const transicoesPossiveis = ref<string[]>([]);

  /**
   * O que ESTA pessoa pode pedir ao agente de chamados neste chamado — vem do
   * backend a cada resposta, como `transicoesPossiveis`. A tela não sabe quem
   * marca nem em que status: só desenha o que chegou.
   */
  const pedidosDeAgente = ref<string[]>([]);

  const franquias = ref<FranquiaDoSeletor[]>([]);

  /** Vem do backend a cada resposta — nunca derivado dos grupos aqui. */
  const souSuporte = ref(false);

  const temChamados = computed(() => chamados.value.length > 0);

  const mensagemDeErro = (e: unknown): string =>
    e instanceof Error ? e.message : String(e);

  const carregarLista = async (novaAba?: AbaChamados): Promise<void> => {
    if (novaAba) aba.value = novaAba;

    carregando.value = true;
    erro.value = null;

    try {
      const resposta = await httpService.get(`${URL_LISTA}?aba=${aba.value}`, {
        responseContract: 'Chamados_ListaResponse',
      });

      if (!resposta.success) {
        throw new Error(resposta.error || 'Erro ao carregar os chamados');
      }

      chamados.value = resposta.dados?.rows ?? [];
      souSuporte.value = resposta.dados?.sou_suporte ?? false;
    } catch (e) {
      logError('useChamados', 'carregarLista', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    } finally {
      carregando.value = false;
    }
  };

  /**
   * Guarda o detalhe vindo de QUALQUER uma das quatro rotas que devolvem a
   * mesma forma (detalhe, abrir, responder, mudar-status).
   *
   * Centralizado porque as três de escrita já devolvem o estado fresco: a tela
   * se redesenha do que o servidor acabou de gravar, em vez de adivinhar o novo
   * estado localmente — que é como uma tela passa a mostrar coisa que o banco
   * não tem.
   */
  const guardarDetalhe = (dados: {
    chamado: Chamados_ChamadoRow;
    mensagens: Chamados_MensagemRow[];
    transicoes_possiveis: string[];
    sou_suporte: boolean;
    pedidos_de_agente?: string[];
  }): void => {
    chamadoAberto.value = dados.chamado;
    mensagens.value = dados.mensagens ?? [];
    transicoesPossiveis.value = dados.transicoes_possiveis ?? [];
    souSuporte.value = dados.sou_suporte ?? false;
    pedidosDeAgente.value = dados.pedidos_de_agente ?? [];
  };

  const abrirDetalhe = async (id: number): Promise<void> => {
    carregando.value = true;
    erro.value = null;

    try {
      const resposta = await httpService.get(`${URL_DETALHE}?id=${id}`, {
        responseContract: 'Chamados_DetalheResponse',
      });

      if (!resposta.success || !resposta.dados) {
        throw new Error(resposta.error || 'Erro ao carregar o chamado');
      }

      guardarDetalhe(resposta.dados);
    } catch (e) {
      logError('useChamados', 'abrirDetalhe', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    } finally {
      carregando.value = false;
    }
  };

  const carregarFranquias = async (): Promise<FranquiaDoSeletor[]> => {
    try {
      const resposta = await httpService.get(URL_FRANQUIAS, {
        responseContract: 'Chamados_FranquiasResponse',
      });

      if (!resposta.success) {
        throw new Error(resposta.error || 'Erro ao carregar as franquias');
      }

      franquias.value = resposta.dados?.rows ?? [];

      return franquias.value;
    } catch (e) {
      logError('useChamados', 'carregarFranquias', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    }
  };

  const abrirChamado = async (novo: NovoChamado): Promise<Chamados_ChamadoRow> => {
    carregando.value = true;
    erro.value = null;

    try {
      const payload: Chamados_AbrirInput = novo;

      const resposta = await httpService.post(URL_ABRIR, payload, {
        inputContract: 'Chamados_AbrirInput',
        responseContract: 'Chamados_DetalheResponse',
      });

      if (!resposta.success || !resposta.dados) {
        throw new Error(resposta.error || 'Erro ao abrir o chamado');
      }

      guardarDetalhe(resposta.dados);

      return resposta.dados.chamado;
    } catch (e) {
      logError('useChamados', 'abrirChamado', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    } finally {
      carregando.value = false;
    }
  };

  /**
   * `interna` só é honrado pelo backend para o suporte. O default é `false`
   * pelo mesmo motivo do backend: errar para "público" mostra ao franqueado uma
   * mensagem que era para ficar interna — constrangedor, mas VISÍVEL; errar
   * para "interno" faz a resposta sumir e ninguém descobre, porque o franqueado
   * só acha que foi ignorado.
   */
  const responder = async (
    chamadoId: number,
    corpo: string,
    interna = false,
  ): Promise<void> => {
    carregando.value = true;
    erro.value = null;

    try {
      const payload: Chamados_ResponderInput = {
        chamado_id: chamadoId,
        corpo,
        interna,
      };

      const resposta = await httpService.post(
        URL_RESPONDER,
        payload,
        {
          inputContract: 'Chamados_ResponderInput',
          responseContract: 'Chamados_DetalheResponse',
        },
      );

      if (!resposta.success || !resposta.dados) {
        throw new Error(resposta.error || 'Erro ao enviar a mensagem');
      }

      guardarDetalhe(resposta.dados);
    } catch (e) {
      logError('useChamados', 'responder', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    } finally {
      carregando.value = false;
    }
  };

  /**
   * Marca o chamado para o agente (`resolver` | `planejar`) ou, com pedido
   * vazio, tira a marca. Devolve o detalhe fresco, como as outras escritas.
   */
  const marcarAgente = async (chamadoId: number, pedido: string): Promise<void> => {
    carregando.value = true;
    erro.value = null;

    try {
      const payload: Chamados_MarcarAgenteInput = {
        chamado_id: chamadoId,
        agente: pedido,
      };

      const resposta = await httpService.post(
        URL_MARCAR_AGENTE,
        payload,
        {
          inputContract: 'Chamados_MarcarAgenteInput',
          responseContract: 'Chamados_DetalheResponse',
        },
      );

      if (!resposta.success || !resposta.dados) {
        throw new Error(resposta.error || 'Erro ao marcar para o agente');
      }

      guardarDetalhe(resposta.dados);
    } catch (e) {
      logError('useChamados', 'marcarAgente', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    } finally {
      carregando.value = false;
    }
  };

  const mudarStatus = async (chamadoId: number, status: string): Promise<void> => {
    carregando.value = true;
    erro.value = null;

    try {
      const payload: Chamados_MudarStatusInput = {
        chamado_id: chamadoId,
        status,
      };

      const resposta = await httpService.post(
        URL_MUDAR_STATUS,
        payload,
        {
          inputContract: 'Chamados_MudarStatusInput',
          responseContract: 'Chamados_DetalheResponse',
        },
      );

      if (!resposta.success || !resposta.dados) {
        throw new Error(resposta.error || 'Erro ao mudar o status');
      }

      guardarDetalhe(resposta.dados);
    } catch (e) {
      logError('useChamados', 'mudarStatus', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    } finally {
      carregando.value = false;
    }
  };

  /**
   * Sobe UMA imagem e pendura na mensagem indicada.
   *
   * Uma chamada por arquivo, e não um multipart com N arquivos: assim uma
   * imagem grande que falha não derruba as outras quatro, e o fio volta
   * atualizado a cada uma (o usuário vê a miniatura aparecer).
   *
   * O backend devolve o DETALHE INTEIRO, igual a `responder`/`mudarStatus` —
   * é o que mantém uma única forma de atualizar o estado da tela.
   */
  const anexarImagem = async (
    chamadoId: number,
    mensagemId: number,
    arquivo: File,
  ): Promise<void> => {
    if (arquivo.size > TAMANHO_MAX_ANEXO) {
      throw new Error(`A imagem "${arquivo.name}" passa de 5 MB`);
    }

    erro.value = null;

    try {
      const formulario = new FormData();
      formulario.append('chamado_id', String(chamadoId));
      formulario.append('mensagem_id', String(mensagemId));
      formulario.append('imagem', arquivo);

      // `inputContract` declarado mesmo sendo multipart: o httpService PULA
      // a validação de forma quando o corpo é FormData (o `_files` do
      // contrato espelha $_FILES e nunca viaja no fio), mas a declaração é o
      // que amarra esta chamada ao contrato do backend — é o mesmo par que
      // `useSOReprocessos` usa para a foto de reprocesso.
      const resposta = await httpService.post(URL_ANEXAR, formulario, {
        inputContract: 'Chamados_AnexarInput',
        responseContract: 'Chamados_DetalheResponse',
      });

      if (!resposta.success || !resposta.dados) {
        throw new Error(resposta.error || 'Erro ao enviar a imagem');
      }

      guardarDetalhe(resposta.dados);
    } catch (e) {
      logError('useChamados', 'anexarImagem', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    }
  };

  /**
   * Sobe uma lista de imagens para a ÚLTIMA mensagem do fio — que é sempre a
   * que o usuário acabou de criar, seja pela abertura ou pela resposta.
   *
   * Em sequência, não em paralelo: em paralelo as N chamadas devolvem o mesmo
   * fio em estados diferentes e a última a chegar (não a última a sair) define
   * o que fica na tela — some anexo por corrida.
   */
  const anexarNaUltimaMensagem = async (
    chamadoId: number,
    arquivos: File[],
  ): Promise<void> => {
    const ultima = mensagens.value[mensagens.value.length - 1];

    if (!ultima) {
      throw new Error('Não há mensagem para anexar a imagem');
    }

    for (const arquivo of arquivos.slice(0, MAX_ANEXOS_POR_MENSAGEM)) {
      await anexarImagem(chamadoId, ultima.id, arquivo);
    }
  };

  /**
   * Pede ao ai-agent-service um RASCUNHO de resposta para o chamado aberto.
   *
   * ---------------------------------------------------------------------------
   * O QUE VIAJA DAQUI É UM `id`, E SÓ
   * ---------------------------------------------------------------------------
   * O texto do chamado NÃO é enviado pela tela. O agente busca o fio no app-api
   * com o JWT do próprio usuário, então quem decide o que ele pode ler continua
   * sendo o backend (`carregarSePuder`).
   *
   * Se o prompt viesse daqui, duas coisas ruins: qualquer um gastaria crédito
   * da franquia com texto arbitrário, e a barreira de posse do chamado deixaria
   * de valer para este caminho.
   *
   * A resposta é TEXTO + LEITURA, não uma ação: nada é enviado, nada muda no
   * chamado. Quem envia é o atendente, pelo `responder` de sempre.
   *
   * A análise vem na MESMA chamada que o rascunho — duas chamadas seriam dois
   * débitos no ledger de créditos e duas viagens ao modelo pelo mesmo fio.
   */
  const sugerirResposta = async (
    chamadoId: number,
  ): Promise<{ sugestao: string; analise?: AnaliseDeChamado }> => {
    erro.value = null;

    // URL ABSOLUTA para o agentHost — o httpService deixa passar sem prefixar
    // o app-api. Mesma convenção do `useAgentChat`.
    const url = `${config.api.agentHost}/chamados/${chamadoId}/sugestao`;

    try {
      // Sem `responseContract`: o destino é o ai-agent-service, que não tem
      // contrato PHP. A forma é garantida por `parseSugestaoDeResposta`, o
      // mesmo desenho das outras chamadas ao agente (useAgentChat e irmãos).
      const bruto = await httpService.post<unknown>(url, {});
      const { sugestao, analise } = parseSugestaoDeResposta(bruto);

      log('useChamados', 'sugerirResposta', 'Rascunho recebido', {
        chamadoId,
        chars: sugestao.length,
        intencao: analise?.intencao ?? null,
      });

      return { sugestao, ...(analise !== undefined ? { analise } : {}) };
    } catch (e) {
      logError('useChamados', 'sugerirResposta', 'Erro', e);
      erro.value = mensagemDeErro(e);
      throw e;
    }
  };

  const fecharDetalhe = (): void => {
    log('useChamados', 'fecharDetalhe', 'Limpando o detalhe aberto');
    chamadoAberto.value = null;
    mensagens.value = [];
    transicoesPossiveis.value = [];
    pedidosDeAgente.value = [];
  };

  return {
    // estado
    carregando,
    erro,
    chamados,
    aba,
    chamadoAberto,
    mensagens,
    transicoesPossiveis,
    pedidosDeAgente,
    franquias,
    souSuporte,
    temChamados,
    // ações
    carregarLista,
    abrirDetalhe,
    carregarFranquias,
    abrirChamado,
    responder,
    mudarStatus,
    marcarAgente,
    anexarImagem,
    anexarNaUltimaMensagem,
    sugerirResposta,
    fecharDetalhe,
  };
}
